import React, { useMemo, useEffect, useState, useRef } from 'react';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from '../../../common/toast';
import logger from '../../../utils/logger';
import { usePersistedDataStore } from '../../store.js';
import { useUser } from '../../UserContext.js';
import { EVIDENCE_TYPES_FOR_SELECTION } from '../../../services/regenerateWorkupsService.js';
import { findWorkupFile } from '../Workups/WorkupVisualization/workupFileHelpers.js';
import {
  EXHIBIT_LABEL_MODES,
  NYSCEF_DOC_TYPES,
  NYSCEF_UPLOAD_METHODS,
  describeDroppedFiles,
  mapFilesToSelectedCases,
  miscZipFilenameExample,
  triggerWorkupRegenerationAndUpload,
  uploadMiscellaneousDocument,
} from './uploadEvidenceService.js';

const EVIDENCE_TYPE_VALUES = EVIDENCE_TYPES_FOR_SELECTION.map((o) => o.value);
const labelFor = (value) => EVIDENCE_TYPES_FOR_SELECTION.find((o) => o.value === value)?.label ?? value;
const UPLOADED_EVIDENCE_LABEL_MAP = { Unequal: 'Sales', Excessive: 'Assessments' };
const UPLOADED_EVIDENCE_VALUE_PRIORITY = ['Unequal', 'Excessive'];

// One fixed width for both methods so the dialog doesn't jump when the case list appears.
const DIALOG_WIDTH = 560;
// Case list scrolls after ~4 rows so it never pushes the rest of the form off screen.
const CASE_LIST_MAX_HEIGHT = 184;
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

const colors = {
  bgPrimary: '#ffffff',
  bgSecondary: '#f1efe8',
  textPrimary: '#1a1a18',
  textSecondary: '#5f5e5a',
  textTertiary: '#888780',
  borderTertiary: 'rgba(0,0,0,0.12)',
  borderSecondary: 'rgba(0,0,0,0.22)',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
  accentBorder: '#B5D4F4',
  warn: '#BA7517',
  error: '#B42318',
  errorBg: '#FEF3F2',
  errorBorder: '#FECDCA',
};

const DIALOG_PAPER_SX = {
  width: '100%',
  borderRadius: '16px',
  border: `0.5px solid ${colors.borderSecondary}`,
  bgcolor: colors.bgPrimary,
  m: 2,
};

const CANCEL_BUTTON_SX = {
  fontSize: 13,
  fontWeight: 500,
  color: colors.textSecondary,
  border: `0.5px solid ${colors.borderSecondary}`,
  borderRadius: '8px',
  px: 2,
  py: 1,
  textTransform: 'none',
};

const DANGER_BUTTON_SX = {
  fontSize: 13,
  fontWeight: 500,
  bgcolor: colors.error,
  borderRadius: '8px',
  px: 2.5,
  py: 1,
  textTransform: 'none',
  '&:hover': { bgcolor: '#912018' },
};

const DOC_TYPE_OPTIONS = [
  { type: NYSCEF_DOC_TYPES.EXHIBIT, label: 'Exhibit', Icon: DescriptionOutlinedIcon },
  { type: NYSCEF_DOC_TYPES.LETTER, label: 'Letter', Icon: MailOutlineIcon },
];

const EXHIBIT_LABEL_OPTIONS = [
  { mode: EXHIBIT_LABEL_MODES.NUMBER, label: 'Number', example: '1' },
  { mode: EXHIBIT_LABEL_MODES.LETTER, label: 'Letter', example: 'A' },
];

const onActivateKey = (fn) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fn();
  }
};

const SectionLabel = ({ children }) => (
  <Typography
    component="p"
    sx={{
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: colors.textTertiary,
      m: '0 0 8px',
    }}
  >
    {children}
  </Typography>
);

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 500,
      color: colors.textTertiary,
      mb: 0.75,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    }}
  >
    {children}
  </Typography>
);

// Icon sits beside the text instead of above it — saves ~28px of height.
const MethodCard = ({ active, badge, icon, name, description, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    aria-pressed={active}
    onKeyDown={onActivateKey(onClick)}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      border: '1.5px solid',
      borderColor: active ? colors.accent : colors.borderTertiary,
      borderRadius: '8px',
      p: '10px 12px',
      cursor: 'pointer',
      transition: 'border-color 0.15s, background 0.15s',
      background: active ? colors.accentBg : colors.bgPrimary,
      '&:hover': { borderColor: active ? colors.accent : colors.borderSecondary },
    }}
  >
    <Box sx={{ display: 'flex', fontSize: 20, mt: '1px', color: active ? colors.accent : colors.textSecondary }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: '2px' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: 0 }}>{name}</Typography>
        {badge && (
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 500,
              px: '6px',
              py: '1px',
              borderRadius: '20px',
              border: `0.5px solid ${colors.accentBorder}`,
              background: colors.accentBg,
              color: colors.accent,
              flexShrink: 0,
            }}
          >
            {badge}
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>{description}</Typography>
    </Box>
  </Box>
);

const OptionsPanel = ({ children }) => (
  <Box sx={{ border: `0.5px solid ${colors.borderTertiary}`, borderRadius: '8px', p: 1.75 }}>{children}</Box>
);

const SegmentedOption = ({ active, disabled, onSelect, leading, label }) => (
  <Box
    onClick={() => !disabled && onSelect()}
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-pressed={active}
    onKeyDown={onActivateKey(() => !disabled && onSelect())}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.25,
      py: 0.625,
      borderRadius: '8px',
      border: '1.5px solid',
      borderColor: active ? colors.accent : colors.borderTertiary,
      bgcolor: active ? colors.accentBg : colors.bgPrimary,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      transition: 'border-color 0.15s, background 0.15s',
      '&:hover': disabled ? {} : { borderColor: active ? colors.accent : colors.borderSecondary },
    }}
  >
    <Box sx={{ display: 'flex', color: active ? colors.accent : colors.textSecondary }}>{leading}</Box>
    <Typography sx={{ fontSize: 12, fontWeight: 500, color: colors.textPrimary, m: 0 }}>{label}</Typography>
  </Box>
);

const LabelBadge = ({ active, children }) => (
  <Box
    sx={{
      width: 20,
      height: 20,
      borderRadius: '5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 11,
      fontWeight: 600,
      fontFamily: MONO,
      color: active ? colors.accent : colors.textSecondary,
      bgcolor: active ? colors.bgPrimary : colors.bgSecondary,
      border: `0.5px solid ${active ? colors.accentBorder : colors.borderTertiary}`,
    }}
  >
    {children}
  </Box>
);

// Opens a preview when url is set; without one it renders a dashed placeholder (e.g. report not generated yet).
const FileChip = ({ label, url, title, muted }) => (
  <Box
    component={url ? 'a' : 'span'}
    {...(url && { href: url, target: '_blank', rel: 'noopener noreferrer' })}
    title={title}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      maxWidth: '100%',
      minWidth: 0,
      px: 0.75,
      py: 0.25,
      fontSize: 12,
      fontWeight: 500,
      textDecoration: 'none',
      borderRadius: '6px',
      border: `0.5px ${url ? 'solid' : 'dashed'} ${muted ? colors.borderSecondary : colors.accentBorder}`,
      color: !url ? colors.textSecondary : muted ? colors.textPrimary : colors.accent,
      bgcolor: colors.bgPrimary,
      '&:hover': url ? { bgcolor: colors.accentBg } : {},
    }}
  >
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </Box>
    {url && <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.7, flexShrink: 0 }} aria-hidden="true" />}
  </Box>
);

const CaseCell = ({ address, parcelID, scarID }) => {
  const meta = [scarID ? `SCAR ${scarID}` : null, address ? parcelID : null].filter(Boolean).join(' · ');
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap title={address || parcelID} sx={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>
        {address || parcelID}
      </Typography>
      {meta && (
        <Typography noWrap title={meta} sx={{ fontSize: 11, color: colors.textSecondary }}>
          {meta}
        </Typography>
      )}
    </Box>
  );
};

/** rows: [{ parcelID, scarID, address, files: [{ key, label, url?, title, muted? }], note?, noteColor?, dim? }] */
const CaseList = ({ summary, summaryColor, sharedFiles, rows, showFiles }) => {
  const rowGrid = {
    display: 'grid',
    gridTemplateColumns: showFiles ? 'minmax(0,1fr) minmax(0,1fr)' : 'minmax(0,1fr)',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: 0.875,
    borderTop: `0.5px solid ${colors.borderTertiary}`,
  };
  return (
    <Box sx={{ border: `0.5px solid ${colors.borderTertiary}`, borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1.5, py: 0.75, bgcolor: colors.bgSecondary }}>
        <Typography sx={{ fontSize: 11, fontWeight: 500, color: colors.textTertiary }}>Cases</Typography>
        <Typography noWrap sx={{ fontSize: 11, fontWeight: 500, color: summaryColor ?? colors.textTertiary, minWidth: 0 }}>
          {summary}
        </Typography>
      </Box>
      {sharedFiles?.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, px: 1.5, py: 1, borderTop: `0.5px solid ${colors.borderTertiary}` }}>
          <Typography sx={{ fontSize: 12, color: colors.textSecondary, flexShrink: 0 }}>Filed on every case:</Typography>
          {sharedFiles.map(({ key, ...chip }) => <FileChip key={key} {...chip} />)}
        </Box>
      )}
      <Box sx={{ maxHeight: CASE_LIST_MAX_HEIGHT, overflowY: 'auto' }}>
        {rows.map((r) => (
          <Box key={r.parcelID} sx={{ ...rowGrid, ...(r.dim && { bgcolor: colors.bgSecondary }) }}>
            <CaseCell address={r.address} parcelID={r.parcelID} scarID={r.scarID} />
            {(showFiles || r.note) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                {r.note && <Typography sx={{ fontSize: 11, fontWeight: 500, color: r.noteColor ?? colors.textTertiary }}>{r.note}</Typography>}
                {r.files.map(({ key, ...chip }) => <FileChip key={key} {...chip} />)}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const PortalSwitch = ({ checked, onChange, disabled }) => (
  <Switch
    size="small"
    checked={checked}
    onChange={onChange}
    disabled={disabled}
    sx={{
      width: 32,
      height: 18,
      p: 0,
      flexShrink: 0,
      mt: '1px',
      '& .MuiSwitch-switchBase': {
        p: 0,
        m: '3px',
        transitionDuration: '150ms',
        '&.Mui-checked': {
          transform: 'translateX(14px)',
          color: '#fff',
          '& + .MuiSwitch-track': { backgroundColor: colors.accent, opacity: 1 },
        },
      },
      '& .MuiSwitch-thumb': { width: 12, height: 12 },
      '& .MuiSwitch-track': {
        borderRadius: 9,
        backgroundColor: colors.borderSecondary,
        opacity: 1,
      },
    }}
  />
);

const getEvidenceSummary = (cases) => {
  if (!cases?.length) return { uniqueTypes: [], countsByType: {} };
  const countsByType = {};
  const seen = new Set();
  for (const c of cases) {
    const types = Array.isArray(c.SelectedEvidenceTypes) ? c.SelectedEvidenceTypes : [];
    for (const t of types) {
      if (t && typeof t === 'string') {
        seen.add(t);
        countsByType[t] = (countsByType[t] || 0) + 1;
      }
    }
  }
  return { uniqueTypes: EVIDENCE_TYPE_VALUES.filter((t) => seen.has(t)), countsByType };
};

/**
 * Confirmation modal for uploading to NYSCEF — evidence reports or miscellaneous PDFs.
 */
const NyscefUploadModal = ({ open, onClose, selectedRows, rows }) => {
  const { user } = useUser();
  const negotiationObj = usePersistedDataStore((s) => s.negotiationObj);
  const isVillageRun = negotiationObj?.villageRun === 1;
  const fileInputRef = useRef(null);

  const [uploadMethod, setUploadMethod] = useState(NYSCEF_UPLOAD_METHODS.EVIDENCE);
  const [selectedEvidenceTypes, setSelectedEvidenceTypes] = useState([]);
  const [confirmAttempted, setConfirmAttempted] = useState(false);
  const [forceUploadDoubleOpen, setForceUploadDoubleOpen] = useState(false);
  const [miscFiles, setMiscFiles] = useState([]);
  const [miscMappings, setMiscMappings] = useState(null);
  const [nyscefDocType, setNyscefDocType] = useState(NYSCEF_DOC_TYPES.EXHIBIT);
  const [miscDescription, setMiscDescription] = useState('');
  const [exhibitLabelMode, setExhibitLabelMode] = useState(EXHIBIT_LABEL_MODES.NUMBER);
  const [miscForceUpload, setMiscForceUpload] = useState(false);
  const [miscUploading, setMiscUploading] = useState(false);
  const [miscZipReading, setMiscZipReading] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [showWarningParcels, setShowWarningParcels] = useState(false);
  const [multiCaseConfirmOpen, setMultiCaseConfirmOpen] = useState(false);
  const [gatedPayload, setGatedPayload] = useState(null);

  const { evidenceSummary, noCourtDateError, noEvidenceCount, inScopeCaseCount, casesInScope } = useMemo(() => {
    const rowsToUse = selectedRows?.length ? selectedRows : rows;
    const parcelIds = new Set(rowsToUse?.map((r) => r.parcel_id).filter(Boolean) ?? []);
    const cases = (negotiationObj?.cases ?? []).filter((c) => parcelIds.has(c.parcel_id));
    const summary = getEvidenceSummary(cases);
    const noIndexNumber = cases.some((c) => !c.SCARIndexNumber || c.SCARIndexNumber === '');
    const caseHasNoEvidence = (c) =>
      !Array.isArray(c.SelectedEvidenceTypes) ||
      !c.SelectedEvidenceTypes.filter((t) => t && typeof t === 'string').length;
    return {
      evidenceSummary: summary,
      noCourtDateError: noIndexNumber,
      noEvidenceCount: cases.filter(caseHasNoEvidence).length,
      inScopeCaseCount: cases.length,
      casesInScope: cases,
    };
  }, [selectedRows, rows, negotiationObj?.cases]);

  const rowsToUseForWarnings = useMemo(() => (selectedRows?.length ? selectedRows : rows), [selectedRows, rows]);
  const uploadedEvidenceWarning = useMemo(() => {
    const rowsList = rowsToUseForWarnings ?? [];
    const caseByParcelId = new Map(
      (negotiationObj?.cases ?? []).map((c) => [c?.parcel_id, c]).filter(([parcelId]) => !!parcelId)
    );

    const matchesByValue = new Map(UPLOADED_EVIDENCE_VALUE_PRIORITY.map((v) => [v, new Set()]));
    const flaggedParcels = [];
    for (const row of rowsList) {
      const parcelId = row?.parcel_id;
      if (!parcelId) continue;
      const caseItem = caseByParcelId.get(parcelId);
      const uploadedEvidence = caseItem?.UploadedEvidence;
      if (uploadedEvidence == null) continue;
      if (!Array.isArray(uploadedEvidence)) {
        flaggedParcels.push({ parcelId, uploadedEvidence, reason: 'not an array' });
        continue;
      }

      const matched = [];
      for (const value of UPLOADED_EVIDENCE_VALUE_PRIORITY) {
        if (uploadedEvidence.includes(value)) {
          matchesByValue.get(value).add(parcelId);
          matched.push(value);
        }
      }
      if (matched.length) {
        flaggedParcels.push({
          parcelId,
          scarIndexNumber: caseItem?.SCARIndexNumber ?? null,
          uploadedEvidence,
          matched,
        });
      }
    }
    if (open && flaggedParcels.length) {
      logger.warn('[NYSCEF] already-uploaded evidence', { parcels: flaggedParcels });
    }

    const value = UPLOADED_EVIDENCE_VALUE_PRIORITY.find((v) => matchesByValue.get(v).size > 0);
    const count = value ? matchesByValue.get(value).size : 0;
    const warningParcelIds = value ? Array.from(matchesByValue.get(value)) : [];
    const scarIndexNumbers = [];
    const seenIndex = new Set();
    const parcels = [];
    for (const pid of warningParcelIds) {
      const n = caseByParcelId.get(pid)?.SCARIndexNumber;
      const s = n != null && String(n).trim() !== '' ? String(n).trim() : null;
      if (s && !seenIndex.has(s)) {
        seenIndex.add(s);
        scarIndexNumbers.push(s);
      }
      parcels.push({ parcelId: pid, scarIndexNumber: s });
    }
    parcels.sort((a, b) => String(a.parcelId).localeCompare(String(b.parcelId), undefined, { numeric: true }));
    return {
      value: value ?? 'Unequal',
      label: value ? UPLOADED_EVIDENCE_LABEL_MAP[value] : UPLOADED_EVIDENCE_LABEL_MAP.Unequal,
      count,
      scarIndexNumbers,
      parcels,
    };
  }, [open, rowsToUseForWarnings, negotiationObj?.cases]);

  const sortedForceUploadIndexNumbers = useMemo(() => {
    const nums = [...(uploadedEvidenceWarning.scarIndexNumbers ?? [])];
    nums.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
    return nums;
  }, [uploadedEvidenceWarning.scarIndexNumbers]);

  useEffect(() => {
    if (open && noCourtDateError) {
      toast.error('A selected case does not have evidence set to upload (or no court date exists).', {
        autoClose: false,
      });
      onClose();
    }
  }, [open, noCourtDateError, onClose]);

  useEffect(() => {
    if (open) {
      setUploadMethod(NYSCEF_UPLOAD_METHODS.EVIDENCE);
      setSelectedEvidenceTypes([]);
      setConfirmAttempted(false);
      setForceUploadDoubleOpen(false);
      setMiscFiles([]);
      setMiscMappings(null);
      setNyscefDocType(NYSCEF_DOC_TYPES.EXHIBIT);
      setMiscDescription('');
      setExhibitLabelMode(EXHIBIT_LABEL_MODES.NUMBER);
      setMiscForceUpload(false);
      setMiscUploading(false);
      setMiscZipReading(false);
      setIsDraggingFile(false);
      setShowWarningParcels(false);
      setMultiCaseConfirmOpen(false);
      setGatedPayload(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else {
      setForceUploadDoubleOpen(false);
      setMultiCaseConfirmOpen(false);
    }
  }, [open]);

  const toggleEvidenceType = (type) => {
    setSelectedEvidenceTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type].sort((a, b) => EVIDENCE_TYPE_VALUES.indexOf(a) - EVIDENCE_TYPE_VALUES.indexOf(b))
    );
  };

  const showNoEvidence = noEvidenceCount > 0;
  const hasOverride = selectedEvidenceTypes.length > 0;
  const needsOverrideValidation = showNoEvidence && !hasOverride;
  const showValidationError = confirmAttempted && needsOverrideValidation;
  const isEvidence = uploadMethod === NYSCEF_UPLOAD_METHODS.EVIDENCE;
  const isMisc = uploadMethod === NYSCEF_UPLOAD_METHODS.MISC;
  const isExhibit = nyscefDocType === NYSCEF_DOC_TYPES.EXHIBIT;

  const completeEvidenceUpload = async (forceOverride = false) => {
    onClose();
    const result = await triggerWorkupRegenerationAndUpload({
      selectedRows,
      rows,
      user,
      forceOverride,
      ...(selectedEvidenceTypes.length > 0 && { evidenceTypes: selectedEvidenceTypes }),
    });
    if (result?.gatedPayload) setGatedPayload(result.gatedPayload);
  };

  const handleEvidenceConfirm = () => {
    if (needsOverrideValidation) {
      setConfirmAttempted(true);
      return;
    }
    if (uploadedEvidenceWarning.count > 0) {
      setForceUploadDoubleOpen(true);
      return;
    }
    completeEvidenceUpload(false);
  };

  const handleMiscConfirm = () => {
    setConfirmAttempted(true);
    if (!miscFiles.length) {
      toast.error('Select a PDF or ZIP to upload.');
      return;
    }
    if (!String(miscDescription || '').trim()) {
      toast.error('Enter a document description.');
      return;
    }
    if (multiCaseFiles.length) {
      setMultiCaseConfirmOpen(true);
      return;
    }
    void runMiscUpload();
  };

  const runMiscUpload = async () => {
    const rowsToUse = selectedRows?.length ? selectedRows : rows;
    setMiscUploading(true);
    try {
      const result = await uploadMiscellaneousDocument({
        rows: rowsToUse,
        user,
        files: miscFiles,
        mappings: miscMappings,
        nyscefDocType,
        description: miscDescription,
        exhibitLabelMode,
        forceOverride: miscForceUpload,
        // testing: true, // REMOVE THIS WHEN WE ARE DONE
      });
      if (result?.gatedPayload) setGatedPayload(result.gatedPayload);
      else if (result?.success) onClose();
    } finally {
      setMiscUploading(false);
    }
  };

  const handleConfirm = () => {
    if (isMisc) {
      handleMiscConfirm();
      return;
    }
    handleEvidenceConfirm();
  };

  const hasEvidenceSet = evidenceSummary.uniqueTypes.length > 0;
  const summaryLines = hasOverride
    ? selectedEvidenceTypes.map((t) => `${inScopeCaseCount} ${labelFor(t)}`)
    : hasEvidenceSet
      ? evidenceSummary.uniqueTypes.map((t) => `${evidenceSummary.countsByType[t] ?? 0} ${labelFor(t)}`)
      : [];

  const scopeLabel = selectedRows?.length
    ? `${selectedRows.length} selected case${selectedRows.length === 1 ? '' : 's'}`
    : `${casesInScope.length} case${casesInScope.length === 1 ? '' : 's'}`;

  const selectedCount = selectedRows?.length ? selectedRows.length : casesInScope.length;
  const singleCaseHeader = useMemo(() => {
    if (selectedCount !== 1) return null;
    const caseItem = casesInScope[0];
    const row = (selectedRows?.length ? selectedRows : rows)?.[0];
    const address = caseItem?.Address || row?.Address || '';
    const scarIndex = caseItem?.SCARIndexNumber || row?.SCARIndexNumber || '';
    return address || scarIndex ? { address, scarIndex } : null;
  }, [selectedCount, casesInScope, selectedRows, rows]);

  const miscBusy = miscUploading || miscZipReading;
  const miscDropTitle = miscZipReading
    ? 'Reading ZIP…'
    : isDraggingFile
      ? 'Drop PDFs or ZIPs here'
      : miscFiles.length === 1
        ? miscFiles[0].name
        : miscFiles.length > 1
          ? `${miscFiles.length} files selected`
          : 'Choose PDFs or ZIPs';
  const miscFileUrls = useMemo(
    () => new Map([...new Set((miscMappings ?? []).map((m) => m.file))].map((f) => [f, URL.createObjectURL(f)])),
    [miscMappings]
  );
  useEffect(() => () => miscFileUrls.forEach((url) => URL.revokeObjectURL(url)), [miscFileUrls]);
  const miscCaseGroups = useMemo(() => {
    const groups = new Map();
    (miscMappings ?? []).forEach((m, idx) => {
      if (!groups.has(m.parcelID)) {
        const address = casesInScope.find((c) => c.parcel_id === m.parcelID)?.Address || '';
        groups.set(m.parcelID, { parcelID: m.parcelID, scarID: m.scarID, address, files: [] });
      }
      const shared = m.matchBy === 'shared';
      groups.get(m.parcelID).files.push({
        key: `${m.filename}-${idx}`,
        label: m.filename,
        url: miscFileUrls.get(m.file),
        shared,
        muted: shared,
        title: `Open ${m.filename} (${shared ? 'filed on every selected case' : `matched by ${m.matchBy === 'parcelID' ? 'parcel ID' : 'SCAR index'}`})`,
      });
    });
    return [...groups.values()];
  }, [miscMappings, miscFileUrls, casesInScope]);
  const multiCaseFiles = useMemo(() => {
    const counts = new Map();
    for (const m of miscMappings ?? []) counts.set(m.file, (counts.get(m.file) || 0) + 1);
    return [...counts].filter(([, n]) => n > 1).map(([file, n]) => ({ name: file.name, caseCount: n }));
  }, [miscMappings]);

  // Shared files go on every case, so show them once instead of repeating them per row.
  const sharedMiscFiles = useMemo(
    () => [...new Map(miscCaseGroups.flatMap((g) => g.files).filter((f) => f.shared).map((f) => [f.url, f])).values()],
    [miscCaseGroups]
  );
  const hasCaseSpecificFiles = miscCaseGroups.some((g) => g.files.some((f) => !f.shared));
  const unmatchedMiscCases = useMemo(() => {
    if (!miscMappings) return [];
    const matched = new Set(miscCaseGroups.map((g) => g.parcelID));
    return casesInScope.filter((c) => !matched.has(c.parcel_id));
  }, [miscMappings, miscCaseGroups, casesInScope]);
  const miscCaseRows = [
    ...miscCaseGroups.map((g) => {
      const files = g.files.filter((f) => !f.shared);
      return { ...g, files, note: hasCaseSpecificFiles && !files.length ? 'Shared file only' : null };
    }),
    ...unmatchedMiscCases.map((c) => ({
      parcelID: c.parcel_id,
      scarID: c.SCARIndexNumber,
      address: c.Address,
      files: [],
      note: "No matching file — won't be filed",
      noteColor: colors.warn,
      dim: true,
    })),
  ];

  const evidenceCaseGroups = useMemo(() => {
    const caseByParcelId = new Map(casesInScope.map((c) => [c.parcel_id, c]));
    return (rowsToUseForWarnings ?? []).map((row) => caseByParcelId.get(row.parcel_id)).filter(Boolean).map((c) => {
      const types = hasOverride ? selectedEvidenceTypes : (c.SelectedEvidenceTypes || []).filter((t) => t && typeof t === 'string');
      return {
        parcelID: c.parcel_id,
        scarID: c.SCARIndexNumber,
        address: c.Address || '',
        note: types.length ? null : 'No evidence set',
        noteColor: colors.warn,
        files: types.map((t) => {
          const url = findWorkupFile(c.S3Files, t)?.Url;
          return {
            key: t,
            label: labelFor(t),
            url,
            muted: !url,
            title: url ? `Preview current ${labelFor(t)} (regenerated before upload)` : `No ${labelFor(t)} on file yet, generated before upload`,
          };
        }),
      };
    });
  }, [casesInScope, rowsToUseForWarnings, hasOverride, selectedEvidenceTypes]);
  const evidenceReportCount = evidenceCaseGroups.reduce((n, g) => n + g.files.length, 0);

  const filingCount = miscMappings?.length ?? 0;
  const miscDropDetail = miscZipReading
    ? 'Mapping files to selected cases'
    : !miscFiles.length
      ? `Drag and drop or click to select PDFs or ZIPs of PDFs, each named like ${miscZipFilenameExample(casesInScope[0]?.SCARIndexNumber, casesInScope[0]?.parcel_id)}`
      : `${filingCount} filing${filingCount === 1 ? '' : 's'} across ${miscCaseGroups.length} of ${selectedCount} case${selectedCount === 1 ? '' : 's'}`;
  const primaryDisabled = miscBusy || (isMisc && !miscFiles.length);
  const footerNote = isMisc
    ? filingCount > 0 ? `${filingCount} filing${filingCount === 1 ? '' : 's'} ready` : ''
    : `${evidenceReportCount} report${evidenceReportCount === 1 ? '' : 's'} to file`;

  const clearMiscFileInput = () => {
    setMiscFiles([]);
    setMiscMappings(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openFilePicker = () => {
    if (!miscBusy) fileInputRef.current?.click();
  };

  const acceptMiscFiles = async (fileList) => {
    const picked = [...(fileList ?? [])].filter(Boolean);
    if (!picked.length) {
      clearMiscFileInput();
      return;
    }
    const invalid = picked.find((file) => {
      const name = String(file.name || '').toLowerCase();
      return !name.endsWith('.pdf') && file.type !== 'application/pdf' && !name.endsWith('.zip');
    });
    if (invalid) {
      toast.error('Only PDF or ZIP files can be filed to NYSCEF.');
      clearMiscFileInput();
      return;
    }
    setMiscZipReading(true);
    try {
      const mapped = await mapFilesToSelectedCases(picked, selectedRows?.length ? selectedRows : rows, negotiationObj);
      if (mapped.error) {
        toast.error(mapped.error, { autoClose: false });
        clearMiscFileInput();
        return;
      }
      if (mapped.dropped?.length) {
        toast.warning(describeDroppedFiles(mapped.dropped, mapped.example), { autoClose: false });
      }
      setMiscFiles(picked);
      setMiscMappings(mapped.mappings);
    } finally {
      setMiscZipReading(false);
    }
  };

  const handleFilePick = (e) => {
    const picked = [...(e.target.files ?? [])];
    e.target.value = '';
    void acceptMiscFiles(picked);
  };

  const isFileDrag = (e) => [...(e.dataTransfer?.types ?? [])].includes('Files');

  const handlePdfDragOver = (e) => {
    if (!isMisc || miscBusy || !isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (!isDraggingFile) setIsDraggingFile(true);
  };

  const handlePdfDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingFile(false);
  };

  const handlePdfDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    if (!isMisc || miscBusy) return;
    void acceptMiscFiles(e.dataTransfer.files);
  };

  return (
    <>
      <StableMuiDialog
        open={open && !noCourtDateError}
        onClose={miscBusy ? undefined : onClose}
        maxWidth={false}
        PaperProps={{
          onDragOver: isMisc ? handlePdfDragOver : undefined,
          onDragEnter: isMisc ? handlePdfDragOver : undefined,
          onDragLeave: isMisc ? handlePdfDragLeave : undefined,
          onDrop: isMisc ? handlePdfDrop : undefined,
          sx: {
            ...DIALOG_PAPER_SX,
            maxWidth: DIALOG_WIDTH,
            // Header + footer stay put; only the middle scrolls.
            maxHeight: 'calc(100% - 32px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, px: 3, pt: 3, pb: 2, flexShrink: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={{ fontSize: 17, fontWeight: 500, color: colors.textPrimary, m: '0 0 4px' }}>
              Upload to NYSCEF
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.textSecondary, m: 0 }}>File documents for {scopeLabel}</Typography>
          </Box>
          {singleCaseHeader && (
            <Box sx={{ textAlign: 'right', minWidth: 0, maxWidth: 220, flexShrink: 0 }}>
              {singleCaseHeader.address ? (
                <Typography noWrap title={singleCaseHeader.address} sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: 0 }}>
                  {singleCaseHeader.address}
                </Typography>
              ) : null}
              {singleCaseHeader.scarIndex ? (
                <Typography sx={{ fontSize: 12, color: colors.textSecondary, m: 0 }}>{singleCaseHeader.scarIndex}</Typography>
              ) : null}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, pb: 2.5 }}>
          <SectionLabel>Upload method</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2.5 }}>
            <MethodCard
              active={isEvidence}
              badge="Default"
              icon={<DescriptionOutlinedIcon fontSize="inherit" />}
              name="Evidence reports"
              description="Sales comp reports / Assessment Equity reports"
              onClick={() => setUploadMethod(NYSCEF_UPLOAD_METHODS.EVIDENCE)}
            />
            <MethodCard
              active={isMisc}
              icon={<UploadFileOutlinedIcon fontSize="inherit" />}
              name="Miscellaneous PDF"
              description="Motions, affidavits, correspondences"
              onClick={() => setUploadMethod(NYSCEF_UPLOAD_METHODS.MISC)}
            />
          </Box>

          <SectionLabel>{isEvidence ? 'Evidence options' : 'Document options'}</SectionLabel>

          {isEvidence && (
            <OptionsPanel>
              <FieldLabel>Evidence type</FieldLabel>
              <FormGroup row sx={{ columnGap: 2 }}>
                {EVIDENCE_TYPES_FOR_SELECTION.map(({ value, label }) => {
                  const disabled = isVillageRun && value === 'Excessive';
                  return (
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedEvidenceTypes.includes(value)}
                          onChange={() => !disabled && toggleEvidenceType(value)}
                          disabled={disabled}
                          sx={{ color: colors.borderSecondary, '&.Mui-checked': { color: colors.accent } }}
                        />
                      }
                      label={<Typography sx={{ fontSize: 13, color: colors.textPrimary }}>{label}</Typography>}
                    />
                  );
                })}
              </FormGroup>
              <Typography
                sx={{
                  fontSize: 11,
                  mb: 1.5,
                  ...(hasOverride || showValidationError
                    ? { color: colors.error, textDecoration: 'underline' }
                    : { color: colors.textSecondary }),
                }}
              >
                {hasOverride
                  ? '*This will override and upload ALL cases with this type'
                  : "Leave unchecked to use each case's evidence from Evidence Comparison, or check a type to override all cases."}
              </Typography>

              <CaseList
                summary={`${summaryLines.length ? summaryLines.join(' · ') : 'No evidence types set'}${showNoEvidence ? ` · ${noEvidenceCount} with none set` : ''}`}
                summaryColor={showNoEvidence ? colors.error : undefined}
                rows={evidenceCaseGroups}
                showFiles
              />
              <Typography sx={{ fontSize: 11, color: colors.textTertiary, mt: 0.75 }}>
                Reports regenerate before upload; previews show the current version.
              </Typography>

              {open && uploadedEvidenceWarning.count > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.25,
                    borderRadius: '8px',
                    bgcolor: colors.errorBg,
                    border: `0.5px solid ${colors.errorBorder}`,
                    display: 'flex',
                    gap: 0.75,
                    alignItems: 'flex-start',
                  }}
                >
                  <WarningAmberOutlinedIcon sx={{ fontSize: 15, color: colors.error, flexShrink: 0, mt: '1px' }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, color: colors.error, fontWeight: 600, lineHeight: 1.4, m: 0 }}>
                      WARNING: There are already{' '}
                      <span style={{ textDecoration: 'underline' }}>{uploadedEvidenceWarning.label}</span> uploaded for{' '}
                      {uploadedEvidenceWarning.count} {uploadedEvidenceWarning.count === 1 ? 'case' : 'cases'}. Confirming
                      will upload an additional document.
                    </Typography>
                    <Button
                      onClick={() => setShowWarningParcels((prev) => !prev)}
                      sx={{
                        mt: 1,
                        fontSize: 12,
                        fontWeight: 500,
                        color: colors.textPrimary,
                        bgcolor: colors.bgPrimary,
                        border: `0.5px solid ${colors.borderSecondary}`,
                        borderRadius: '8px',
                        px: 1.25,
                        py: 0.4,
                        textTransform: 'none',
                        minWidth: 'auto',
                        lineHeight: 1.4,
                        '&:hover': { bgcolor: colors.bgSecondary },
                      }}
                    >
                      {showWarningParcels ? 'Hide parcels' : 'Show parcels'}
                    </Button>
                    {showWarningParcels && (
                      <Box sx={{ mt: 0.75, maxHeight: 120, overflowY: 'auto' }}>
                        {uploadedEvidenceWarning.parcels.map((p) => (
                          <Typography key={p.parcelId} sx={{ fontSize: 12, fontWeight: 500, color: colors.error, lineHeight: 1.5, m: 0 }}>
                            {p.parcelId}
                            {p.scarIndexNumber ? ` · ${p.scarIndexNumber}` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </OptionsPanel>
          )}

          {isMisc && (
            <OptionsPanel>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf,application/zip,.zip"
                multiple
                hidden
                onChange={handleFilePick}
              />

              {!miscFiles.length ? (
                <Box
                  onClick={openFilePicker}
                  role="button"
                  tabIndex={0}
                  onKeyDown={onActivateKey(openFilePicker)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    border: `1.5px dashed ${isDraggingFile ? colors.accent : confirmAttempted ? colors.error : colors.borderSecondary}`,
                    borderRadius: '8px',
                    p: '14px 16px',
                    cursor: miscBusy ? 'not-allowed' : 'pointer',
                    bgcolor: isDraggingFile ? colors.accentBg : colors.bgPrimary,
                    transition: 'border-color 0.15s, background 0.15s',
                    '&:hover': miscBusy ? {} : { borderColor: colors.accent },
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, mt: '1px', color: isDraggingFile ? colors.accent : colors.textTertiary }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: 0 }}>{miscDropTitle}</Typography>
                    <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4, wordBreak: 'break-word' }}>
                      {miscDropDetail}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  onClick={openFilePicker}
                  role="button"
                  tabIndex={0}
                  onKeyDown={onActivateKey(openFilePicker)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: '8px 6px 8px 12px',
                    borderRadius: '8px',
                    border: `1.5px solid ${isDraggingFile ? colors.accent : colors.accentBorder}`,
                    bgcolor: colors.accentBg,
                    cursor: miscBusy ? 'not-allowed' : 'pointer',
                    transition: 'border-color 0.15s',
                    '&:hover': miscBusy ? {} : { borderColor: colors.accent },
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: colors.accent, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap title={miscFiles.map((f) => f.name).join('\n')} sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary }}>
                      {miscDropTitle}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: 11, color: colors.textSecondary }}>
                      {miscDropDetail}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: colors.accent, flexShrink: 0, px: 0.5 }}>Replace</Typography>
                  <IconButton
                    size="small"
                    aria-label="Remove selected files"
                    disabled={miscBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      clearMiscFileInput();
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ color: colors.textSecondary, flexShrink: 0 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}

              {miscCaseGroups.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <CaseList
                    summary={`${miscCaseGroups.length} of ${selectedCount} matched`}
                    summaryColor={unmatchedMiscCases.length ? colors.warn : undefined}
                    sharedFiles={sharedMiscFiles}
                    rows={miscCaseRows}
                    showFiles={hasCaseSpecificFiles}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 1.5, mt: 2, mb: 2 }}>
                <Box>
                  <FieldLabel>Document type</FieldLabel>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {DOC_TYPE_OPTIONS.map(({ type, label, Icon }) => (
                      <SegmentedOption
                        key={type}
                        active={nyscefDocType === type}
                        disabled={miscBusy}
                        onSelect={() => setNyscefDocType(type)}
                        leading={<Icon sx={{ fontSize: 18 }} />}
                        label={label}
                      />
                    ))}
                  </Box>
                </Box>
                {isExhibit && (
                  <Box>
                    <FieldLabel>Exhibit label</FieldLabel>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {EXHIBIT_LABEL_OPTIONS.map(({ mode, label, example }) => {
                        const active = exhibitLabelMode === mode;
                        return (
                          <SegmentedOption
                            key={mode}
                            active={active}
                            disabled={miscBusy}
                            onSelect={() => setExhibitLabelMode(mode)}
                            leading={<LabelBadge active={active}>{example}</LabelBadge>}
                            label={label}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Document Description"
                placeholder="e.g. Motion to Preclude"
                value={miscDescription}
                onChange={(e) => setMiscDescription(e.target.value)}
                disabled={miscBusy}
                error={confirmAttempted && !String(miscDescription || '').trim()}
                helperText={confirmAttempted && !String(miscDescription || '').trim() ? 'Required' : 'Shown below the document type on NYSCEF'}
                sx={{
                  '& .MuiInputBase-root': { fontSize: 13, borderRadius: '8px' },
                  '& .MuiInputLabel-root': { fontSize: 13 },
                  '& .MuiFormHelperText-root': { fontSize: 11, mx: 0 },
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mt: 1.75, pt: 1.5, borderTop: `0.5px solid ${colors.borderTertiary}` }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 2px' }}>Force upload</Typography>
                  <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>
                    File even if it looks like a duplicate of an already-filed document
                  </Typography>
                </Box>
                <PortalSwitch checked={miscForceUpload} onChange={(e) => setMiscForceUpload(e.target.checked)} disabled={miscBusy} />
              </Box>
            </OptionsPanel>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 2, borderTop: `0.5px solid ${colors.borderTertiary}`, flexShrink: 0 }}>
          <Typography noWrap sx={{ fontSize: 12, color: colors.textSecondary, mr: 'auto', minWidth: 0 }}>
            {footerNote}
          </Typography>
          <Button onClick={onClose} disabled={miscBusy} sx={{ ...CANCEL_BUTTON_SX, minWidth: 'auto', flexShrink: 0 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disableElevation
            disabled={primaryDisabled}
            sx={{
              fontSize: 13,
              fontWeight: 500,
              bgcolor: colors.accent,
              borderRadius: '8px',
              px: 2.5,
              py: 1,
              textTransform: 'none',
              minWidth: 'auto',
              flexShrink: 0,
              '&:hover': { bgcolor: '#144f8a' },
            }}
          >
            {miscZipReading ? 'Reading ZIP…' : miscUploading ? 'Uploading…' : isMisc ? 'Upload' : 'Confirm'}
          </Button>
        </Box>
      </StableMuiDialog>

      <StableMuiDialog
        open={forceUploadDoubleOpen}
        onClose={() => setForceUploadDoubleOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: { ...DIALOG_PAPER_SX, maxWidth: 460 } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, color: colors.error, m: '0 0 8px' }}>
            Confirm forced evidence upload
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 12px' }}>
            Confirm you intend to force an additional evidence upload.
          </Typography>
          {sortedForceUploadIndexNumbers.length > 0 ? (
            <>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 1 }}>
                If you are unsure what already exists, look up{' '}
                {sortedForceUploadIndexNumbers.length === 1 ? 'this case' : 'these cases'} on NYSCEF using the index
                number{sortedForceUploadIndexNumbers.length === 1 ? '' : 's'} below.
              </Typography>
              <Box
                sx={{
                  height: 200,
                  overflowY: 'auto',
                  border: `0.5px solid ${colors.borderTertiary}`,
                  borderRadius: '8px',
                  px: 1.5,
                  py: 1,
                  bgcolor: colors.bgSecondary,
                  mb: 2,
                }}
              >
                {sortedForceUploadIndexNumbers.map((num, idx) => (
                  <Box key={`${String(num)}-${idx}`} sx={{ display: 'flex', gap: 1.25, py: 0.625, alignItems: 'baseline' }}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 12,
                        color: colors.textSecondary,
                        minWidth: '2.25rem',
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}.
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 12, fontFamily: MONO, fontWeight: 500, wordBreak: 'break-all' }}>
                      {num}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 2 }}>
              If you are unsure what already exists, look up the selected case(s) on NYSCEF.
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setForceUploadDoubleOpen(false)} sx={CANCEL_BUTTON_SX}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                setForceUploadDoubleOpen(false);
                completeEvidenceUpload(true);
              }}
              sx={DANGER_BUTTON_SX}
            >
              Yes — force upload
            </Button>
          </Box>
        </Box>
      </StableMuiDialog>

      <StableMuiDialog
        open={multiCaseConfirmOpen}
        onClose={() => setMultiCaseConfirmOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: { ...DIALOG_PAPER_SX, maxWidth: 460 } }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberOutlinedIcon sx={{ fontSize: 22, color: colors.error }} />
            <Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, color: colors.error, m: 0 }}>
              File the same document on multiple cases?
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: colors.textPrimary, mb: 1.5 }}>
            {multiCaseFiles.length === 1 ? 'This file matched' : 'These files matched'} no case by name and will be filed on{' '}
            <strong>every selected case</strong>. Files matched to a single case are not affected.
          </Typography>
          <Box sx={{ p: 1.25, mb: 2, borderRadius: '8px', bgcolor: colors.errorBg, border: `0.5px solid ${colors.errorBorder}`, maxHeight: 160, overflowY: 'auto' }}>
            {multiCaseFiles.map((f) => (
              <Typography key={f.name} sx={{ fontSize: 12, fontWeight: 600, color: colors.error, lineHeight: 1.6, wordBreak: 'break-all' }}>
                {f.name} → {f.caseCount} cases
              </Typography>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setMultiCaseConfirmOpen(false)} sx={CANCEL_BUTTON_SX}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                setMultiCaseConfirmOpen(false);
                void runMiscUpload();
              }}
              sx={DANGER_BUTTON_SX}
            >
              Yes — file on {multiCaseFiles.reduce((max, f) => Math.max(max, f.caseCount), 0)} cases
            </Button>
          </Box>
        </Box>
      </StableMuiDialog>

      <StableMuiDialog
        open={!!gatedPayload}
        onClose={() => setGatedPayload(null)}
        maxWidth={false}
        PaperProps={{ sx: { ...DIALOG_PAPER_SX, maxWidth: 760 } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, color: colors.textPrimary, m: '0 0 4px' }}>
            Upload gated — nothing was sent
          </Typography>
          <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 1.5 }}>
            NYSCEF_UPLOAD_GATE is on in uploadEvidenceService.js. Below is everything that would have been uploaded and sent.
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              mb: 2,
              p: 1.5,
              maxHeight: '60vh',
              overflow: 'auto',
              fontSize: 11,
              lineHeight: 1.5,
              fontFamily: MONO,
              bgcolor: colors.bgSecondary,
              border: `0.5px solid ${colors.borderTertiary}`,
              borderRadius: '8px',
            }}
          >
            {JSON.stringify(gatedPayload, null, 2)}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              onClick={() => navigator.clipboard.writeText(JSON.stringify(gatedPayload, null, 2)).then(() => toast.success('Payload copied.'))}
              sx={CANCEL_BUTTON_SX}
            >
              Copy JSON
            </Button>
            <Button onClick={() => setGatedPayload(null)} sx={CANCEL_BUTTON_SX}>
              Close
            </Button>
          </Box>
        </Box>
      </StableMuiDialog>
    </>
  );
};

export default NyscefUploadModal;
